'use client';

import { SingleStoreType } from '@workspace/interfaces/store-locator';
import { Typography } from '@workspace/ui/common';
import { Key, useCallback, useEffect, useRef } from 'react';
import { StoreCard } from './StoreCard';
import { cn } from '@workspace/lib';
import { AutoSizer, List } from 'react-virtualized';
import 'react-virtualized/styles.css';

type Props = {
  stores: SingleStoreType[];
  selectedKey: string | null;
  onSelectStoreAction: (store: SingleStoreType) => void;
  directionLabel: string | null | undefined;
  notfoundHeading?: string;
  notfoundMessage?: string;
  onClearFilters?: () => void;
};

const storeKeyOf = (store: SingleStoreType, index: number) =>
  (store as any)?._id || (store as any)?.objectID || String(index);

/**
 * Virtualised store list rendered from data already fetched via GROQ.
 * Algolia-free counterpart of ListStoreInstantSearch.
 */
export default function ListStore({
  stores,
  selectedKey,
  onSelectStoreAction,
  directionLabel,
  notfoundHeading,
  notfoundMessage,
  onClearFilters,
}: Props) {
  const listRef = useRef<List>(null);

  useEffect(() => {
    if (selectedKey) return;
    listRef.current?.scrollToRow(0);
  }, [stores, selectedKey]);

  const renderRow = useCallback(
    ({ index, style, key }: { index: number; style: React.CSSProperties; key: Key }) => {
      const store = stores[index];
      const isSelected = selectedKey === storeKeyOf(store, index);

      return (
        <div style={style} key={key}>
          <div onClick={() => onSelectStoreAction(store)}>
            <StoreCard
              className={cn(
                'cursor-pointer rounded-md p-4 lg:px-lg lg:py-md h-49.5',
                isSelected ? 'border-2 border-secondary-300' : 'border border-secondary-100',
              )}
              storeCard={store}
              directionLabel={directionLabel}
            />
          </div>
        </div>
      );
    },
    [stores, selectedKey, onSelectStoreAction, directionLabel],
  );

  if (!stores.length) {
    return (
      <div className="flex flex-col items-center gap-sm">
        <Typography as="p" variant="h6" className="leading-6.5">
          {notfoundHeading}
        </Typography>
        <Typography variant="bodyMd">{notfoundMessage}</Typography>
        {onClearFilters && (
          <button type="button" onClick={onClearFilters} className="text-sm underline text-primary">
            Clear filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="h-full">
      <AutoSizer>
        {({ height, width }) => (
          <List
            ref={listRef}
            height={height}
            width={width}
            rowCount={stores.length}
            rowHeight={214} // Fixed height: StoreCard (~198px) + margin (16px)
            rowRenderer={renderRow}
            className="scrollbar-primary pr-sm max-md:pl-md md:pr-sm"
          />
        )}
      </AutoSizer>
    </div>
  );
}
